/** Shared onboarding keys for retail item rows (Spa / Golf / Retail). */
export const RETAIL_ITEM_FIELD_NUMBER = "Item Number";
export const RETAIL_ITEM_FIELD_DESCRIPTION = "Item Description";

export const DEFAULT_RETAIL_ITEM_NUMBER = 10;
export const DEFAULT_RETAIL_ITEM_DESCRIPTION = "Retail Item";

/** Spa service-linked retail items must use a category under this group. */
export const SPA_SERVICES_CATEGORY_GROUP = "Spa Services";

export type DefaultRetailItemRecord = {
  id?: number;
  [RETAIL_ITEM_FIELD_NUMBER]: number | string;
  [RETAIL_ITEM_FIELD_DESCRIPTION]: string;
};

/** Shared placeholder retail item (Golf rates / legacy API helpers only). */
export const DEFAULT_RETAIL_ITEMS: DefaultRetailItemRecord[] = [
  {
    id: 1,
    [RETAIL_ITEM_FIELD_NUMBER]: DEFAULT_RETAIL_ITEM_NUMBER,
    [RETAIL_ITEM_FIELD_DESCRIPTION]: DEFAULT_RETAIL_ITEM_DESCRIPTION,
  },
];

const isSampleTestRow = (row: Record<string, unknown>): boolean =>
  Object.values(row).some((value) => {
    if (typeof value === "string") {
      return value.toLowerCase().includes("sample_test_agilysys");
    }
    if (Array.isArray(value)) {
      return value.some(
        (item) =>
          typeof item === "string" &&
          item.toLowerCase().includes("sample_test_agilysys"),
      );
    }
    return false;
  });

const hasConfiguredRetailItemNumber = (row: unknown): boolean => {
  if (!row || typeof row !== "object") {
    return false;
  }
  const record = row as Record<string, unknown>;
  for (const key of [RETAIL_ITEM_FIELD_NUMBER, "Retail Item Number"]) {
    const value = record[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return true;
    }
  }
  return false;
};

/** User-configured Retail Items Setup rows only (no synthetic defaults). */
export const resolveRetailItems = (
  payloadItems: unknown,
): DefaultRetailItemRecord[] => {
  if (!Array.isArray(payloadItems)) {
    return [];
  }

  return payloadItems.filter(
    (row) =>
      hasConfiguredRetailItemNumber(row) &&
      !isSampleTestRow(row as Record<string, unknown>),
  ) as DefaultRetailItemRecord[];
};

export const getRetailItemNumber = (
  row: Record<string, unknown>,
): string | null => {
  for (const key of [RETAIL_ITEM_FIELD_NUMBER, "Retail Item Number"]) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
};

export const getRetailItemDescription = (
  row: Record<string, unknown>,
): string | null => {
  const value = row[RETAIL_ITEM_FIELD_DESCRIPTION];
  if (value === undefined || value === null || String(value).trim() === "") {
    return null;
  }
  return String(value).trim();
};

/** `"<number> - <description>"` options for autocomplete fields. */
export const buildRetailItemNumberOptions = (payloadItems: unknown): string[] =>
  resolveRetailItems(payloadItems)
    .map((row) => {
      const num = getRetailItemNumber(row as Record<string, unknown>);
      if (!num) {
        return null;
      }
      const desc = getRetailItemDescription(row as Record<string, unknown>);
      return desc ? `${num} - ${desc}` : num;
    })
    .filter((value): value is string => Boolean(value));

export const getDefaultRetailItemNumber = (): number =>
  DEFAULT_RETAIL_ITEM_NUMBER;

/** Item number from an option like `"100 - Massage"`; null when empty. */
export const parseRetailItemNumberFromOption = (
  option: string | null | undefined,
): string | null => {
  if (!option) {
    return null;
  }
  const separator = option.indexOf(" - ");
  const code = (separator >= 0 ? option.slice(0, separator) : option).trim();
  return code || null;
};

/**
 * Prefer the first Retail Items Setup option.
 * Returns null when no retail items are configured.
 */
export const getLinkedRetailItemDefault = (
  options: string[] | null | undefined,
): string | null => parseRetailItemNumberFromOption(options?.[0]);

/** Resolve Autocomplete display value by exact item-number match (not prefix). */
export const linkedRetailItemOptionValue = (
  itemNumber: string | null | undefined,
  options: string[],
): string | null => {
  if (itemNumber == null || String(itemNumber).trim() === "") {
    return null;
  }
  const normalized = String(itemNumber).trim();
  const match = options.find((option) => {
    const code = parseRetailItemNumberFromOption(option);
    return code === normalized || option === normalized;
  });
  return match ?? null;
};
